import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import axios from "axios";
import { FaTrash, FaPlus, FaUndo } from "react-icons/fa";
import { useNotification } from "../../../../context/NotificationContext";

const MoveRoomPopup = ({ bookingId, onSubmit, bookingData, isOpen, onClose }) => {
  const [formValues, setFormValues] = useState({});
  const [availableRooms, setAvailableRooms] = useState([]);
  const [assignedRooms, setAssignedRooms] = useState([]);
  const [newRooms, setNewRooms] = useState([]);
  const [roomsToRemove, setRoomsToRemove] = useState([]);
  const [rateInfo, setRateInfo] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [taxes, setTaxes] = useState([]); // Store dynamic taxes from API
  const [taxRules, setTaxRules] = useState([]); // Store tax allocation rules
  const modalRef = useRef(null);
  const submitButtonRef = useRef(null);
  const { notify } = useNotification();

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const companyid = localStorage.getItem("companyid") || 61;

  useEffect(() => {
    if (isOpen && bookingId) {
      fetchTaxData();
      fetchAssignedRooms();
      fetchAvailableRooms();
    }
  }, [isOpen, bookingId]);

  // Fetch taxes and tax rules concurrently
  const fetchTaxData = async () => {
    try {
      setLoading(true);
      const [taxRulesResponse, taxesResponse] = await Promise.all([
        axios.get(`${apiUrl}/hmstax/tax_allocation/listing`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyid },
        }),
        axios.get(`${apiUrl}/hmstax/listing`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyid },
        }),
      ]);
      if (taxRulesResponse.data.success && Array.isArray(taxRulesResponse.data.allocations)) {
        setTaxRules(taxRulesResponse.data.allocations.flatMap(allocation => allocation.rules));
      } else {
        notify("error","Failed to fetch tax rules.");
        setTaxRules([]);
      }
      if (taxesResponse.data.success && Array.isArray(taxesResponse.data.taxes)) {
        const currentDate = new Date();
        const applicableTaxes = taxesResponse.data.taxes
          .filter((tax) => {
            const startDate = new Date(tax.start_date);
            const endDate = tax.end_date ? new Date(tax.end_date) : null;
            return (
              tax.applicable_to === "room_charge" &&
              tax.is_active &&
              tax.tax_type === "percentage" &&
              currentDate >= startDate &&
              (endDate === null || currentDate <= endDate)
            );
          })
          .map((tax) => ({
            tax_id: tax.tax_id,
            taxName: tax.tax_name === "GST" ? "CGST" : tax.tax_name,
            taxRate: parseFloat(tax.tax_rate),
          }));
        setTaxes(applicableTaxes);
      } else {
        console.error("Failed to fetch taxes:", taxesResponse.data);
        setTaxes([]);
      }
    } catch (error) {
      console.error("Error fetching tax data:", error);
      notify("error","Error fetching tax data.");
      setTaxRules([]);
      setTaxes([]);
    } finally {
      setLoading(false);
    }
  };

  const initializeForm = (rooms) => {
    const initialValues = {};
    rooms.forEach((room) => {
      const roomTypeId = room.room_type_id || bookingData.roomTypeId; // Use room-specific or booking-level roomTypeId
      const applicableRules = taxRules.filter(rule => {
        const roomMatch = rule.all_room_types
          ? !rule.excluded_room_types.includes(roomTypeId)
          : rule.room_types.includes(roomTypeId);
        return roomMatch;
      });
      const taxIds = [...new Set(applicableRules.flatMap(rule => rule.taxes))];
      const roomCharges = parseFloat(room.room_rate) || 0;
      const taxDetails = taxes
        .filter(tax => taxIds.includes(tax.tax_id))
        .map(tax => ({
          taxName: tax.taxName,
          taxRate: tax.taxRate,
          taxAmount: Number((roomCharges * tax.taxRate / 100).toFixed(2)),
        }));

      initialValues[room.room_number] = {
        newRoom: null,
        charges: parseFloat(room.room_rate) || 0,
        overwriteCharges: false,
        taxDetails: taxDetails, // Initialize with per-room tax breakdown
      };
    });
    setFormValues(initialValues);
  };

  const fetchAssignedRooms = async () => {
    try {
      const response = await axios.get(`${apiUrl}/booking/distinct-assigned-rooms/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setAssignedRooms(response.data.assignedRooms);
        initializeForm(response.data.assignedRooms);
      }
    } catch (error) {
      notify("error","Failed to fetch assigned rooms.");
      console.error("Error fetching assigned rooms:", error);
    }
  };

  const fetchAvailableRooms = async () => {
    try {
      const response = await axios.post(
        `${apiUrl}/booking/roomno-availability`,
        {
          check_in: bookingData.checkIn,
          check_out: bookingData.checkOut,
          room_type_ids: [],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Transform response into grouped options for react-select
      const groupedOptions = response.data.availableRooms.map((category) => ({
        label: `${category.room_type_name} (${category.available_rooms})`,
        options: category.available_room_numbers.map((roomNumber) => ({
          value: roomNumber,
          label: roomNumber,
          roomType: category.room_type_name,
          rate: parseFloat(category.min_rate_single),
          roomTypeId: category.room_type_id, // Assume room_type_id is available
        })),
      }));

      setAvailableRooms(groupedOptions);
    } catch (error) {
      notify("error","Failed to fetch available rooms.");
      setAvailableRooms([]);
    }
  };

  const handleChange = (roomNumber, field, value) => {
    setFormValues((prev) => {
      const updatedRoom = { ...prev[roomNumber] };
      let roomTypeId = assignedRooms.find(r => r.room_number === roomNumber)?.room_type_id || 
                       (newRooms.find(r => r.id === roomNumber) ? 
                        availableRooms.flatMap(g => g.options).find(o => o.value === value?.value)?.roomTypeId : null);
      const applicableRules = taxRules.filter(rule => {
        const roomMatch = rule.all_room_types
          ? !rule.excluded_room_types.includes(roomTypeId)
          : rule.room_types.includes(roomTypeId);
        return roomMatch;
      });
      const taxIds = [...new Set(applicableRules.flatMap(rule => rule.taxes))];

      if (field === "charges") {
        updatedRoom[field] = Number(value) || 0;
        if (!updatedRoom.overwriteCharges) {
          updatedRoom.taxDetails = taxes
            .filter(tax => taxIds.includes(tax.tax_id))
            .map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: Number((updatedRoom[field] * tax.taxRate / 100).toFixed(2)),
            }));
        }
      } else if (field === "newRoom" && value) {
        const selectedRoom = availableRooms
          .flatMap((group) => group.options)
          .find((r) => r.value === value.value);
        updatedRoom[field] = value;
        roomTypeId = selectedRoom?.roomTypeId; // Update roomTypeId based on new room
        if (selectedRoom && selectedRoom.rate !== updatedRoom.charges) {
          setRateInfo((prev) => ({
            ...prev,
            [roomNumber]: {
              newRate: selectedRoom.rate,
              newRoomType: selectedRoom.roomType,
              rateFetched: true,
            },
          }));
          if (!updatedRoom.overwriteCharges) {
            updatedRoom.charges = selectedRoom.rate;
            updatedRoom.taxDetails = taxes
              .filter(tax => taxIds.includes(tax.tax_id))
              .map(tax => ({
                taxName: tax.taxName,
                taxRate: tax.taxRate,
                taxAmount: Number((selectedRoom.rate * tax.taxRate / 100).toFixed(2)),
              }));
          }
        } else {
          setRateInfo((prev) => ({
            ...prev,
            [roomNumber]: {
              newRate: null,
              newRoomType: null,
              rateFetched: false,
            },
          }));
        }
      } else if (field === "overwriteCharges") {
        updatedRoom[field] = value;
        if (!value && formValues[roomNumber]?.newRoom) {
          const selectedRoom = availableRooms
            .flatMap((group) => group.options)
            .find((r) => r.value === formValues[roomNumber].newRoom.value);
          updatedRoom.charges = selectedRoom?.rate || updatedRoom.charges;
          updatedRoom.taxDetails = taxes
            .filter(tax => taxIds.includes(tax.tax_id))
            .map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: Number((updatedRoom.charges * tax.taxRate / 100).toFixed(2)),
            }));
        }
      } else {
        updatedRoom[field] = value;
      }

      return { ...prev, [roomNumber]: updatedRoom };
    });
    setErrors((prev) => ({ ...prev, [roomNumber]: { ...prev[roomNumber], [field]: "" } }));
  };

  const handleRemoveRoom = (roomNumber) => {
    if (assignedRooms.length - roomsToRemove.length <= 1) {
      notify("error","Cannot remove the last room.");
      return;
    }
    setRoomsToRemove((prev) => [...prev, roomNumber]);
  };

  const handleUndoRemove = (roomNumber) => {
    setRoomsToRemove((prev) => prev.filter((r) => r !== roomNumber));
  };

  const handleAddRoom = () => {
    const newRoomId = `new-${Date.now()}`;
    setNewRooms((prev) => [...prev, { id: newRoomId, roomNumber: null, roomType: null, charges: 0 }]);
    setFormValues((prev) => ({
      ...prev,
      [newRoomId]: { newRoom: null, charges: 0, overwriteCharges: false, taxDetails: [] },
    }));
  };

  const handleRemoveNewRoom = (newRoomId) => {
    setNewRooms((prev) => prev.filter((room) => room.id !== newRoomId));
    setFormValues((prev) => {
      const { [newRoomId]: _, ...rest } = prev;
      return rest;
    });
    setRateInfo((prev) => {
      const { [newRoomId]: _, ...rest } = prev;
      return rest;
    });
    setErrors((prev) => {
      const { [newRoomId]: _, ...rest } = prev;
      return rest;
    });
  };

  const validateForm = () => {
    let validationErrors = {};
    assignedRooms.forEach((room) => {
      const roomKey = room.room_number;
      if (!roomsToRemove.includes(roomKey)) {
        if (formValues[roomKey]?.newRoom && formValues[roomKey].newRoom.value === room.room_number) {
          validationErrors[roomKey] = { newRoom: "Cannot select the same room." };
        }
        if (formValues[roomKey]?.overwriteCharges && !formValues[roomKey]?.charges) {
          validationErrors[roomKey] = { ...validationErrors[roomKey], charges: "Charges required when overwriting" };
        } else if (formValues[roomKey]?.charges && (isNaN(formValues[roomKey].charges) || formValues[roomKey].charges <= 0)) {
          validationErrors[roomKey] = { ...validationErrors[roomKey], charges: "Charges must be positive" };
        }
      }
    });
    newRooms.forEach((room) => {
      const roomKey = room.id;
      if (!formValues[roomKey]?.newRoom) {
        validationErrors[roomKey] = { newRoom: "Please select a room." };
      }
      if (formValues[roomKey]?.overwriteCharges && !formValues[roomKey]?.charges) {
        validationErrors[roomKey] = { ...validationErrors[roomKey], charges: "Charges required when overwriting" };
      } else if (formValues[roomKey]?.charges && (isNaN(formValues[roomKey].charges) || formValues[roomKey].charges <= 0)) {
        validationErrors[roomKey] = { ...validationErrors[roomKey], charges: "Charges must be positive" };
      }
    });
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      notify("error","Please fix validation errors");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        bookingId: bookingId,
        roomUpdates: [
          ...assignedRooms.map((room) => {
            const roomData = formValues[room.room_number];
            const newRoomInfo = roomData?.newRoom
              ? availableRooms.flatMap((group) => group.options).find((r) => r.value === roomData.newRoom.value)
              : null;
            const roomTypeId = room.room_type_id || (newRoomInfo ? newRoomInfo.roomTypeId : bookingData.roomTypeId);
            const applicableRules = taxRules.filter(rule => {
              const roomMatch = rule.all_room_types
                ? !rule.excluded_room_types.includes(roomTypeId)
                : rule.room_types.includes(roomTypeId);
              return roomMatch;
            });
            const taxIds = [...new Set(applicableRules.flatMap(rule => rule.taxes))];
            const charges = roomData.overwriteCharges ? roomData.charges : newRoomInfo?.rate || parseFloat(room.room_rate);
            const taxDetails = taxes
              .filter(tax => taxIds.includes(tax.tax_id))
              .map(tax => ({
                taxName: tax.taxName,
                taxRate: tax.taxRate,
                taxAmount: Number((charges * tax.taxRate / 100).toFixed(2)),
              }));

            return {
              currentRoomNumber: room.room_number,
              currentRoomType: room.room_type,
              newRoomNumber: roomData?.newRoom?.value || null,
              newRoomType: newRoomInfo?.roomType || null,
              newRoomTypeId: roomTypeId,
              price: charges,
              taxDetails: taxDetails, // Include tax breakdown
              action: roomsToRemove.includes(room.room_number) ? "remove" : "update",
            };
          }),
          ...newRooms.map((newRoom) => {
            const roomData = formValues[newRoom.id];
            const newRoomInfo = availableRooms
              .flatMap((group) => group.options)
              .find((r) => r.value === roomData.newRoom.value);
            const roomTypeId = newRoomInfo?.roomTypeId;
            const applicableRules = taxRules.filter(rule => {
              const roomMatch = rule.all_room_types
                ? !rule.excluded_room_types.includes(roomTypeId)
                : rule.room_types.includes(roomTypeId);
              return roomMatch;
            });
            const taxIds = [...new Set(applicableRules.flatMap(rule => rule.taxes))];
            const charges = roomData.overwriteCharges ? roomData.charges : newRoomInfo.rate;
            const taxDetails = taxes
              .filter(tax => taxIds.includes(tax.tax_id))
              .map(tax => ({
                taxName: tax.taxName,
                taxRate: tax.taxRate,
                taxAmount: Number((charges * tax.taxRate / 100).toFixed(2)),
              }));

            return {
              newRoomNumber: roomData.newRoom.value,
              newRoomType: newRoomInfo.roomType,
              newRoomTypeId: roomTypeId,
              price: charges,
              taxDetails: taxDetails, // Include tax breakdown for new room
              action: "add",
            };
          }),
        ],
      };

      console.log("Payload sent to backend:", payload); // Debug payload
      const response = await axios.put(`${apiUrl}/booking/upgrade-rooms`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success","Rooms updated successfully!");
      if (onSubmit) onSubmit(response.data);
      onClose();
      if (submitButtonRef.current) {
        submitButtonRef.current.click();
      }
    } catch (error) {
      notify("error","Failed to update rooms.");
      if (error.response?.data?.message === "Room unavailable") {
        notify("error","Selected room unavailable. Please choose another.");
        fetchAvailableRooms();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const isCheckedOutOrCancelledOrNoShow = ["checked-out", "cancelled", "noshow"].includes(
    bookingData?.bookingStatus
  );
  const disableFields = isCheckedOutOrCancelledOrNoShow;

  const getAvailableOptions = (currentRoomNumber, isNewRoom = false) => {
    const selectedRooms = [
      ...assignedRooms.map((r) => formValues[r.room_number]?.newRoom?.value).filter(Boolean),
      ...newRooms.map((nr) => formValues[nr.id]?.newRoom?.value).filter(Boolean),
    ];
    return availableRooms.map((group) => ({
      ...group,
      options: group.options.filter(
        (option) =>
          option.value !== currentRoomNumber &&
          !selectedRooms.includes(option.value) &&
          !(isNewRoom && assignedRooms.some((r) => r.room_number === option.value))
      ),
    }));
  };

  return (
    <div ref={modalRef} id="move_room_all" className="modal custom-modal fade" tabIndex="-1" role="dialog">
      <div
        className="modal-dialog modal-lg"
        style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "60%" }}
        role="document"
      >
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div className="modal-content h-100">
            <div className="modal-header" style={{ backgroundColor: "#f0f8ff", borderBottom: "1px solid #e0e7ff" }}>
              <h5 className="modal-title" style={{ fontWeight: "600", color: "#333" }}>
                Manage Rooms
              </h5>
              <button type="button" className="btn-close" onClick={handleCancel} />
            </div>

            <div className="modal-body" style={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
              <div className="row g-2">
                <div className="col-12 mb-2">
                  <div className="card" style={{ border: "1px solid #e0e7ff", padding: "10px" }}>
                    <h6 className="card-title mb-2">Guest Details</h6>
                    <div className="row g-0">
                      <div className="col-6 pe-2">
                        <p><strong>Folio No:</strong> {bookingData?.bookingNumber || "N/A"}</p>
                        <p>
                          <strong>Arrival:</strong>{" "}
                          {new Date(bookingData?.checkIn).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }) || "N/A"}
                        </p>
                        <p>
                          <strong>Departure:</strong>{" "}
                          {new Date(bookingData?.checkOut).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }) || "N/A"}
                        </p>
                      </div>
                      <div className="col-6 ps-2">
                        <p><strong>Status:</strong> {bookingData?.bookingStatus || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="card" style={{ border: "1px solid #e0e7ff", padding: "10px" }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="card-title mb-0">Room Updates</h6>
                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm"
                        onClick={handleAddRoom}
                        disabled={disableFields || availableRooms.length === 0}
                      >
                        <FaPlus /> Add Room
                      </button>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr style={{ backgroundColor: "#e0e7ff" }}>
                            <th>Current Type</th>
                            <th>Current Room</th>
                            <th>New Room</th>
                            <th>Charges</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignedRooms.map((room, index) => {
                            const availableOptions = getAvailableOptions(room.room_number);
                            const rateDifference =
                              formValues[room.room_number]?.newRoom && rateInfo[room.room_number]?.newRate
                                ? rateInfo[room.room_number].newRate - parseFloat(room.room_rate)
                                : 0;
                            const isRemoved = roomsToRemove.includes(room.room_number);
                            return (
                              <tr
                                key={index}
                                style={{ textDecoration: isRemoved ? "line-through" : "none", opacity: isRemoved ? 0.5 : 1 }}
                              >
                                <td>{room.room_type}</td>
                                <td>{room.room_number}</td>
                                <td>
                                  <Select
                                    value={formValues[room.room_number]?.newRoom || null}
                                    onChange={(option) => handleChange(room.room_number, "newRoom", option)}
                                    options={availableOptions}
                                    isClearable
                                    placeholder={
                                      availableOptions.some((group) => group.options.length > 0)
                                        ? "Select Room"
                                        : "No Room Available"
                                    }
                                    isDisabled={
                                      disableFields ||
                                      !availableOptions.some((group) => group.options.length > 0) ||
                                      isRemoved
                                    }
                                    styles={{
                                      control: (provided) => ({
                                        ...provided,
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        backgroundColor:
                                          disableFields ||
                                          !availableOptions.some((group) => group.options.length > 0) ||
                                          isRemoved
                                            ? "#f8f9fa"
                                            : "white",
                                      }),
                                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                    }}
                                  />
                                  {rateInfo[room.room_number]?.newRoomType && !isRemoved && (
                                    <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                      New Type: {rateInfo[room.room_number].newRoomType}
                                    </div>
                                  )}
                                  {errors[room.room_number]?.newRoom && !isRemoved && (
                                    <div className="invalid-feedback d-block" style={{ color: "#dc3545" }}>
                                      {errors[room.room_number].newRoom}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={formValues[room.room_number]?.charges || 0}
                                    onChange={(e) => handleChange(room.room_number, "charges", e.target.value)}
                                    disabled={
                                      !(formValues[room.room_number]?.overwriteCharges || formValues[room.room_number]?.newRoom) ||
                                      isRemoved
                                    }
                                    min="0"
                                    step="0.01"
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      border: "1px solid #e0e0e0",
                                      backgroundColor:
                                        !(formValues[room.room_number]?.overwriteCharges || formValues[room.room_number]?.newRoom) || isRemoved
                                          ? "#f8f9fa"
                                          : "white",
                                      width: "100px",
                                    }}
                                  />
                                  <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                    Current Rate: {parseFloat(room.room_rate).toFixed(2)}
                                  </div>
                                  {rateInfo[room.room_number]?.newRate && !isRemoved && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: rateDifference >= 0 ? "#dc3545" : "#28a745",
                                        marginTop: "4px",
                                      }}
                                    >
                                      Difference: {rateDifference >= 0 ? "+" : ""}{rateDifference.toFixed(2)}
                                    </div>
                                  )}
                                  <div className="d-flex align-items-center mt-1">
                                    <label className="switch" style={{ marginRight: "8px" }}>
                                      <input
                                        type="checkbox"
                                        checked={formValues[room.room_number]?.overwriteCharges || false}
                                        onChange={(e) => handleChange(room.room_number, "overwriteCharges", e.target.checked)}
                                        disabled={disableFields || isRemoved}
                                      />
                                      <span className="slider round"></span>
                                    </label>
                                    <span>Overwrite Charges</span>
                                  </div>
                                  {errors[room.room_number]?.charges && !isRemoved && (
                                    <div className="invalid-feedback d-block" style={{ color: "#dc3545" }}>
                                      {errors[room.room_number].charges}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {isRemoved ? (
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={() => handleUndoRemove(room.room_number)}
                                      disabled={disableFields}
                                      title="Undo Remove"
                                    >
                                      <FaUndo />
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleRemoveRoom(room.room_number)}
                                      disabled={disableFields || assignedRooms.length - roomsToRemove.length <= 1}
                                      title="Remove Room"
                                    >
                                      <FaTrash />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {newRooms.map((newRoom, index) => {
                            const availableOptions = getAvailableOptions(null, true);
                            const rateDifference =
                              formValues[newRoom.id]?.newRoom && rateInfo[newRoom.id]?.newRate
                                ? rateInfo[newRoom.id].newRate
                                : 0;
                            return (
                              <tr key={newRoom.id} style={{ backgroundColor: "#f0fff0" }}>
                                <td>New</td>
                                <td>-</td>
                                <td>
                                  <Select
                                    value={formValues[newRoom.id]?.newRoom || null}
                                    onChange={(option) => handleChange(newRoom.id, "newRoom", option)}
                                    options={availableOptions}
                                    isClearable
                                    placeholder={
                                      availableOptions.some((group) => group.options.length > 0)
                                        ? "Select Room"
                                        : "No Room Available"
                                    }
                                    isDisabled={
                                      disableFields || !availableOptions.some((group) => group.options.length > 0)
                                    }
                                    styles={{
                                      control: (provided) => ({
                                        ...provided,
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        backgroundColor:
                                          disableFields || !availableOptions.some((group) => group.options.length > 0)
                                            ? "#f8f9fa"
                                            : "white",
                                      }),
                                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                    }}
                                  />
                                  {rateInfo[newRoom.id]?.newRoomType && (
                                    <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                      Type: {rateInfo[newRoom.id].newRoomType}
                                    </div>
                                  )}
                                  {errors[newRoom.id]?.newRoom && (
                                    <div className="invalid-feedback d-block" style={{ color: "#dc3545" }}>
                                      {errors[newRoom.id].newRoom}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={formValues[newRoom.id]?.charges || 0}
                                    onChange={(e) => handleChange(newRoom.id, "charges", e.target.value)}
                                    disabled={!(formValues[newRoom.id]?.overwriteCharges || formValues[newRoom.id]?.newRoom)}
                                    min="0"
                                    step="0.01"
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      border: "1px solid #e0e0e0",
                                      backgroundColor: !(formValues[newRoom.id]?.overwriteCharges || formValues[newRoom.id]?.newRoom)
                                        ? "#f8f9fa"
                                        : "white",
                                      width: "100px",
                                    }}
                                  />
                                  {rateInfo[newRoom.id]?.newRate && (
                                    <div style={{ fontSize: "12px", color: "#28a745", marginTop: "4px" }}>
                                      Rate: {rateInfo[newRoom.id].newRate.toFixed(2)}
                                    </div>
                                  )}
                                  <div className="d-flex align-items-center mt-1">
                                    <label className="switch" style={{ marginRight: "8px" }}>
                                      <input
                                        type="checkbox"
                                        checked={formValues[newRoom.id]?.overwriteCharges || false}
                                        onChange={(e) => handleChange(newRoom.id, "overwriteCharges", e.target.checked)}
                                        disabled={disableFields}
                                      />
                                      <span className="slider round"></span>
                                    </label>
                                    <span>Overwrite Charges</span>
                                  </div>
                                  {errors[newRoom.id]?.charges && (
                                    <div className="invalid-feedback d-block" style={{ color: "#dc3545" }}>
                                      {errors[newRoom.id].charges}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleRemoveNewRoom(newRoom.id)}
                                    disabled={disableFields}
                                    title="Remove New Room"
                                  >
                                    <FaTrash />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ backgroundColor: "#f0f8ff" }}>
              <button type="submit" className="btn btn-primary" disabled={loading || disableFields}>
                Save
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>

            <div style={{ display: "none" }}>
              <button ref={submitButtonRef} data-bs-dismiss="modal" aria-label="Close" type="reset">
                Submit2
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MoveRoomPopup;